<script setup lang="ts">
import { addressTypes, errToStr } from '@/helpers/tools'
import ConfirmDlg from '@/components/confirm-dlg.vue'
import KpToolbar from '@/components/kp-toolbar.vue'
import { Adresse, AdresseTyp } from '@/models/Adresse'
import OrganisationCard from '@/views/auth/adressen/OrganisationCard.vue'
import PersonCard from '@/views/auth/adressen/PersonCard.vue'
import { useRoute } from 'vue-router'
import { useToast } from '@/composables/toast'
import { createModel } from '@kp/js-model'
import { computed, onMounted, ref, watch, type Ref } from 'vue'
import { useAuthUser } from '@/helpers/globals'

/**
 * is the (inner) AddressForm (See AddressFormPanel): It knows several modes:
 * - standard mode is editing a single address: It loads an address from the API to
 *   edit it.
 * - If it is a new address (route: /new, or some other considerations, see isNew()), no address is loaded from the backend
 * - if the property "newAddress" is set, the form is in the New-Organisation-Mode: It then is only
 *    meant to edit the given organisation record, to create one in-place. No address is loaded.
 * - If in profile mode, the form is displayed unter the "Profile" function.
 */

const {
    newAddress = null,
    gesuchAddress = null,
    avoidTypeEdit = false,
    reducedContactData = false,
    avoidBenutzerIdEdit = false,
    loading = false,
    showInDialog = false,
} = defineProps<{
    newAddress?: Adresse | null
    gesuchAddress?: Adresse | null
    avoidTypeEdit?: boolean
    reducedContactData?: boolean
    avoidBenutzerIdEdit?: boolean
    loading?: boolean
    showInDialog?: boolean
}>()

function isDirty() {
    return adresse.value ? adresse.value.isDirty() : false
}

defineExpose({ isDirty })

const route = useRoute()
const toast = useToast()

const emit = defineEmits<{
    (e: 'update:address', value: Adresse | null): void
    (e: 'close', value: Adresse | null): void
    (e: 'reloadProfile'): void
}>()

const confirmDelDlg = ref(false)
const adresse: Ref<Adresse | null> = ref(null)
const adrLoading = ref(true)
const personValid = ref(false)
const organisationValid = ref(false)
const origAnsprechsPersonEmail: Ref<string | null> = ref(null)
const isNew = computed(() => {
    return (
        newAddress !== null ||
        route.params.id === 'new' ||
        (route.params.id === 'profil' && adresse.value?.$.typ === null)
    )
})
const isProfil = computed(() => {
    return route.params.id === 'profil'
})
const valid = computed(() => {
    if (adresse.value?.typ === 'person') {
        return personValid.value
    }
    if (adresse.value?.typ === 'organisation') {
        return organisationValid.value
    }
    return false
})

watch(route, (value) => {
    // We already are loaded, do nothing:
    if (adresse.value && adresse.value?.id === Number.parseInt(value.params.id as string, 10)) {
        return
    }
    loadRecord(route.params.id as string)
})

watch(adresse, (newAddress, oldAddress) => {
    if (newAddress !== oldAddress) {
        emit('update:address', newAddress)
    }
})

onMounted(() => {
    if (newAddress) {
        adresse.value = newAddress
        adrLoading.value = false
    } else if (gesuchAddress) {
        adresse.value = createModel(Adresse, gesuchAddress)
        adrLoading.value = false
    } else {
        loadRecord(route.params.id as string)
    }
    origAnsprechsPersonEmail.value = adresse.value?.email || null
})

async function onSubmit() {
    cleanAddress()
    cleanBenutzerId()
    if (gesuchAddress) {
        emit('close', adresse.value)
    } else {
        try {
            await adresse.value?.save()
            if (isProfil.value) {
                emit('reloadProfile')
            }
            emit('close', adresse.value)
        } catch (err) {
            toast.add({ text: errToStr(err), color: 'error' })
        }
    }
}
async function loadRecord(id: number | string) {
    if (id === 'new') {
        adresse.value = createModel(Adresse)
        adrLoading.value = false
        return
    }

    const user = useAuthUser()
    const idNum = id === 'profil' ? user?.adresse_id : parseInt(id as string, 10)
    adrLoading.value = true
    try {
        adresse.value = await Adresse.loadById(idNum || -1)
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
    } finally {
        adrLoading.value = false
    }
}
async function onDestroy() {
    confirmDelDlg.value = true
}

function closeForm() {
    emit('close', null)
}

async function confirmDestroy() {
    try {
        await adresse.value?.destroy()
        adresse.value = null
        closeForm()
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    }
}

function typeChanged(value: AdresseTyp) {
    if (value === AdresseTyp.PERSON) {
        adresse.value?.set('email', adresse.value?.email || adresse.value?.organisation_email)
        adresse.value?.set('tel_1', adresse.value?.tel_1 || adresse.value?.organisation_tel_1)
        adresse.value?.set('tel_2', adresse.value?.tel_2 || adresse.value?.organisation_tel_2)
    } else {
        adresse.value?.set(
            'organisation_email',
            adresse.value?.organisation_email || adresse.value?.email,
        )
        adresse.value?.set(
            'organisation_tel_1',
            adresse.value?.organisation_tel_1 || adresse.value?.tel_1,
        )
        adresse.value?.set(
            'organisation_tel_2',
            adresse.value?.organisation_tel_2 || adresse.value?.tel_2,
        )
    }
}
function cleanAddress() {
    if (adresse.value?.typ === 'person') {
        adresse.value?.set('organisation_email', null)
        adresse.value?.set('organisation_tel_1', null)
        adresse.value?.set('organisation_tel_2', null)
        adresse.value?.set('organisation_name', null)
    } else if (!gesuchAddress) {
        adresse.value?.set('email', null)
        adresse.value?.set('tel_1', null)
        adresse.value?.set('tel_2', null)
        adresse.value?.set('vorname', null)
        adresse.value?.set('nachname', null)
    }
}
function cleanBenutzerId() {
    if (
        !avoidBenutzerIdEdit &&
        adresse.value?.typ === 'organisation' &&
        origAnsprechsPersonEmail.value !== adresse.value.email
    ) {
        adresse.value.benutzer_id = null
    }
}
</script>
<template>
    <v-skeleton-loader
        v-if="!adresse || adrLoading"
        class="centered-card"
        type="list-item-avatar, list-item-three-line@3, actions"
    ></v-skeleton-loader>
    <template v-else>
        <KpToolbar icon="far fa-address-book" :title="$t('adresse')"> </KpToolbar>
        <v-card
            :class="{ 'centered-card': !showInDialog }"
            class="d-flex flex-column"
            style="max-height: 100%"
        >
            <v-card-text class="overflow-y-auto">
                <div v-if="!avoidTypeEdit && !isProfil" class="ml-4 mb-4">
                    <div class="text-subtitle-1 text-medium-emphasis">{{ $t('adresstyp') }}</div>
                    <v-radio-group
                        v-model:model-value="adresse.typ"
                        class="mt-0"
                        dense
                        inline
                        hide-details
                        :mandatory="true"
                        @change="typeChanged"
                    >
                        <v-radio
                            :label="$t(addressTypes.person.lang_key)"
                            :value="addressTypes.person.value"
                        ></v-radio>
                        <v-radio
                            :label="$t(addressTypes.organisation.lang_key)"
                            :value="addressTypes.organisation.value"
                        ></v-radio>
                    </v-radio-group>
                </div>
                <PersonCard
                    v-if="adresse.typ === 'person'"
                    :adresse="adresse"
                    :is-profil="isProfil"
                    :loading="adrLoading"
                    :is-new="isNew"
                    :reduced-contact-data="reducedContactData"
                    @validchange="(v: boolean) => (personValid = v)"
                ></PersonCard>
                <OrganisationCard
                    v-if="adresse.typ === 'organisation'"
                    :adresse="adresse"
                    :is-profil="isProfil"
                    :loading="adrLoading"
                    :is-new="isNew"
                    :gesuch-context="!!gesuchAddress"
                    @validchange="(v: boolean) => (organisationValid = v)"
                ></OrganisationCard>
            </v-card-text>
            <v-card-actions>
                <v-btn
                    v-if="!isNew && !gesuchAddress"
                    class="hidden-sm-and-up"
                    color="red"
                    :disabled="loading"
                    text
                    @click="onDestroy"
                >
                    <v-icon>fas fa-trash</v-icon>
                </v-btn>
                <v-btn
                    v-if="!isNew && !isProfil && !gesuchAddress"
                    class="hidden-xs"
                    color="red"
                    :disabled="loading"
                    text
                    @click="onDestroy"
                >
                    {{ $t('delete') }}
                </v-btn>

                <v-spacer />
                <v-btn
                    v-if="!isNew || !isProfil"
                    class="hidden-sm-and-up"
                    color="decline"
                    text
                    :disabled="loading"
                    icon="fas fa-arrow-left"
                    @click="closeForm"
                >
                </v-btn>
                <v-btn
                    v-if="!isNew || !isProfil"
                    class="hidden-xs"
                    color="decline"
                    :disabled="loading"
                    text
                    @click="closeForm"
                >
                    {{ $t('close') }}
                </v-btn>
                <v-btn
                    class="hidden-xs"
                    color="action white--text"
                    type="submit"
                    variant="flat"
                    :disabled="!valid || loading"
                    :loading="loading"
                    prepend-icon="fa-solid fa-floppy-disk"
                    @click="onSubmit"
                >
                    {{ $t('save') }}
                </v-btn>
                <v-btn
                    class="hidden-sm-and-up"
                    color="action white--text"
                    type="submit"
                    variant="flat"
                    :disabled="!valid || loading"
                    :loading="loading"
                    @click="onSubmit"
                    icon="fa-solid fa-floppy-disk"
                >
                </v-btn>
            </v-card-actions>
            <ConfirmDlg
                v-model:model-value="confirmDelDlg"
                :title="$t('bestaetigung')"
                :text="$t('really_delete_rec')"
                @yes="confirmDestroy"
            />
        </v-card>
    </template>
</template>
